"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

var _Fill = _interopRequireDefault(require("./Fill.js"));

var _TextPlacement = _interopRequireDefault(require("./TextPlacement.js"));

var _size = require("../size.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/style/Text
 */

/**
 * The default fill color to use if no fill was set at construction time; a
 * blackish `#333`.
 *
 * @const {string}
 */
var DEFAULT_FILL_COLOR = '#333';
/**
 * @typedef {Object} Options
 * @property {string} [font] Font style as CSS 'font' value, see:
 * https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/font. Default is '10px sans-serif'
 * @property {number} [maxAngle=Math.PI/4] When `placement` is set to `'line'`, allow a maximum angle between adjacent characters.
 * The expected value is in radians, and the default is 45° (`Math.PI / 4`).
 * @property {number} [offsetX=0] Horizontal text offset in pixels. A positive will shift the text right.
 * @property {number} [offsetY=0] Vertical text offset in pixels. A positive will shift the text down.
 * @property {boolean} [overflow=false] For polygon labels or when `placement` is set to `'line'`, allow text to exceed
 * the width of the polygon at the label position or the length of the path that it follows.
 * @property {import("./TextPlacement.js").default|string} [placement='point'] Text placement.
 * @property {number|import("../size.js").Size} [scale] Scale.
 * @property {boolean} [rotateWithView=false] Whether to rotate the text with the view.
 * @property {number} [rotation=0] Rotation in radians (positive rotation clockwise).
 * @property {string|Array<string>} [text] Text content or rich text content. For plain text provide a string, which can
 * contain line breaks (`\n`). For rich text provide an array of text/font tuples. A tuple consists of the text to
 * render and the font to use (or `''` to use the text style's font). A line break has to be a separate tuple (i.e. `'\n', ''`).
 * **Example:** `['foo', 'bold 10px sans-serif', ' bar', 'italic 10px sans-serif', ' baz', '']` will yield "**foo** *bar* baz".
 * **Note:** Rich text is not supported for the immediate rendering API.
 * @property {string} [textAlign] Text alignment. Possible values: 'left', 'right', 'center', 'end' or 'start'.
 * Default is 'center' for `placement: 'point'`. For `placement: 'line'`, the default is to let the renderer choose a
 * placement where `maxAngle` is not exceeded.
 * @property {string} [textBaseline='middle'] Text base line. Possible values: 'bottom', 'top', 'middle', 'alphabetic',
 * 'hanging', 'ideographic'.
 * @property {import("./Fill.js").default} [fill] Fill style. If none is provided, we'll use a dark fill-style (#333).
 * @property {import("./Stroke.js").default} [stroke] Stroke style.
 * @property {import("./Fill.js").default} [backgroundFill] Fill style for the text background when `placement` is
 * `'point'`. Default is no fill.
 * @property {import("./Stroke.js").default} [backgroundStroke] Stroke style for the text background  when `placement`
 * is `'point'`. Default is no stroke.
 * @property {Array<number>} [padding=[0, 0, 0, 0]] Padding in pixels around the text for decluttering and background. The order of
 * values in the array is `[top, right, bottom, left]`.
 */

/**
 * @classdesc
 * Set text style for vector features.
 * @api
 */

var Text =
/** @class */
function () {
  /**
   * @param {Options} [opt_options] Options.
   */
  function Text(opt_options) {
    var options = opt_options || {};
    /**
     * @private
     * @type {string|undefined}
     */

    this.font_ = options.font;
    /**
     * @private
     * @type {number|undefined}
     */

    this.rotation_ = options.rotation;
    /**
     * @private
     * @type {boolean|undefined}
     */

    this.rotateWithView_ = options.rotateWithView;
    /**
     * @private
     * @type {number|import("../size.js").Size|undefined}
     */

    this.scale_ = options.scale;
    /**
     * @private
     * @type {import("../size.js").Size}
     */

    this.scaleArray_ = (0, _size.toSize)(options.scale !== undefined ? options.scale : 1);
    /**
     * @private
     * @type {string|Array<string>|undefined}
     */

    this.text_ = options.text;
    /**
     * @private
     * @type {string|undefined}
     */

    this.textAlign_ = options.textAlign;
    /**
     * @private
     * @type {string|undefined}
     */

    this.textBaseline_ = options.textBaseline;
    /**
     * @private
     * @type {import("./Fill.js").default}
     */

    this.fill_ = options.fill !== undefined ? options.fill : new _Fill["default"]({
      color: DEFAULT_FILL_COLOR
    });
    /**
     * @private
     * @type {number}
     */

    this.maxAngle_ = options.maxAngle !== undefined ? options.maxAngle : Math.PI / 4;
    /**
     * @private
     * @type {import("./TextPlacement.js").default|string}
     */

    this.placement_ = options.placement !== undefined ? options.placement : _TextPlacement["default"].POINT;
    /**
     * @private
     * @type {boolean}
     */

    this.overflow_ = !!options.overflow;
    /**
     * @private
     * @type {import("./Stroke.js").default}
     */

    this.stroke_ = options.stroke !== undefined ? options.stroke : null;
    /**
     * @private
     * @type {number}
     */

    this.offsetX_ = options.offsetX !== undefined ? options.offsetX : 0;
    /**
     * @private
     * @type {number}
     */

    this.offsetY_ = options.offsetY !== undefined ? options.offsetY : 0;
    /**
     * @private
     * @type {import("./Fill.js").default}
     */

    this.backgroundFill_ = options.backgroundFill ? options.backgroundFill : null;
    /**
     * @private
     * @type {import("./Stroke.js").default}
     */

    this.backgroundStroke_ = options.backgroundStroke ? options.backgroundStroke : null;
    /**
     * @private
     * @type {Array<number>|null}
     */

    this.padding_ = options.padding === undefined ? null : options.padding;
  }
  /**
   * Clones the style.
   * @return {Text} The cloned style.
   * @api
   */


  Text.prototype.clone = function () {
    var scale = this.getScale();
    return new Text({
      font: this.getFont(),
      placement: this.getPlacement(),
      maxAngle: this.getMaxAngle(),
      overflow: this.getOverflow(),
      rotation: this.getRotation(),
      rotateWithView: this.getRotateWithView(),
      scale: Array.isArray(scale) ? scale.slice() : scale,
      text: this.getText(),
      textAlign: this.getTextAlign(),
      textBaseline: this.getTextBaseline(),
      fill: this.getFill() ? this.getFill().clone() : undefined,
      stroke: this.getStroke() ? this.getStroke().clone() : undefined,
      offsetX: this.getOffsetX(),
      offsetY: this.getOffsetY(),
      backgroundFill: this.getBackgroundFill() ? this.getBackgroundFill().clone() : undefined,
      backgroundStroke: this.getBackgroundStroke() ? this.getBackgroundStroke().clone() : undefined,
      padding: this.getPadding() || undefined
    });
  };
  /**
   * Get the `overflow` configuration.
   * @return {boolean} Let text overflow the length of the path they follow.
   * @api
   */


  Text.prototype.getOverflow = function () {
    return this.overflow_;
  };
  /**
   * Get the font name.
   * @return {string|undefined} Font.
   * @api
   */


  Text.prototype.getFont = function () {
    return this.font_;
  };
  /**
   * Get the maximum angle between adjacent characters.
   * @return {number} Angle in radians.
   * @api
   */


  Text.prototype.getMaxAngle = function () {
    return this.maxAngle_;
  };
  /**
   * Get the label placement.
   * @return {import("./TextPlacement.js").default|string} Text placement.
   * @api
   */


  Text.prototype.getPlacement = function () {
    return this.placement_;
  };
  /**
   * Get the x-offset for the text.
   * @return {number} Horizontal text offset.
   * @api
   */


  Text.prototype.getOffsetX = function () {
    return this.offsetX_;
  };
  /**
   * Get the y-offset for the text.
   * @return {number} Vertical text offset.
   * @api
   */


  Text.prototype.getOffsetY = function () {
    return this.offsetY_;
  };
  /**
   * Get the fill style for the text.
   * @return {import("./Fill.js").default} Fill style.
   * @api
   */


  Text.prototype.getFill = function () {
    return this.fill_;
  };
  /**
   * Determine whether the text rotates with the map.
   * @return {boolean|undefined} Rotate with map.
   * @api
   */


  Text.prototype.getRotateWithView = function () {
    return this.rotateWithView_;
  };
  /**
   * Get the text rotation.
   * @return {number|undefined} Rotation.
   * @api
   */


  Text.prototype.getRotation = function () {
    return this.rotation_;
  };
  /**
   * Get the text scale.
   * @return {number|import("../size.js").Size|undefined} Scale.
   * @api
   */


  Text.prototype.getScale = function () {
    return this.scale_;
  };
  /**
   * Get the symbolizer scale array.
   * @return {import("../size.js").Size} Scale array.
   */


  Text.prototype.getScaleArray = function () {
    return this.scaleArray_;
  };
  /**
   * Get the stroke style for the text.
   * @return {import("./Stroke.js").default} Stroke style.
   * @api
   */


  Text.prototype.getStroke = function () {
    return this.stroke_;
  };
  /**
   * Get the text to be rendered.
   * @return {string|Array<string>|undefined} Text.
   * @api
   */


  Text.prototype.getText = function () {
    return this.text_;
  };
  /**
   * Get the text alignment.
   * @return {string|undefined} Text align.
   * @api
   */


  Text.prototype.getTextAlign = function () {
    return this.textAlign_;
  };
  /**
   * Get the text baseline.
   * @return {string|undefined} Text baseline.
   * @api
   */


  Text.prototype.getTextBaseline = function () {
    return this.textBaseline_;
  };
  /**
   * Get the background fill style for the text.
   * @return {import("./Fill.js").default} Fill style.
   * @api
   */


  Text.prototype.getBackgroundFill = function () {
    return this.backgroundFill_;
  };
  /**
   * Get the background stroke style for the text.
   * @return {import("./Stroke.js").default} Stroke style.
   * @api
   */


  Text.prototype.getBackgroundStroke = function () {
    return this.backgroundStroke_;
  };
  /**
   * Get the padding for the text.
   * @return {Array<number>|null} Padding.
   * @api
   */


  Text.prototype.getPadding = function () {
    return this.padding_;
  };
  /**
   * Set the `overflow` property.
   *
   * @param {boolean} overflow Let text overflow the path that it follows.
   * @api
   */


  Text.prototype.setOverflow = function (overflow) {
    this.overflow_ = overflow;
  };
  /**
   * Set the font.
   *
   * @param {string|undefined} font Font.
   * @api
   */


  Text.prototype.setFont = function (font) {
    this.font_ = font;
  };
  /**
   * Set the maximum angle between adjacent characters.
   *
   * @param {number} maxAngle Angle in radians.
   * @api
   */


  Text.prototype.setMaxAngle = function (maxAngle) {
    this.maxAngle_ = maxAngle;
  };
  /**
   * Set the x offset.
   *
   * @param {number} offsetX Horizontal text offset.
   * @api
   */


  Text.prototype.setOffsetX = function (offsetX) {
    this.offsetX_ = offsetX;
  };
  /**
   * Set the y offset.
   *
   * @param {number} offsetY Vertical text offset.
   * @api
   */


  Text.prototype.setOffsetY = function (offsetY) {
    this.offsetY_ = offsetY;
  };
  /**
   * Set the text placement.
   *
   * @param {import("./TextPlacement.js").default|string} placement Placement.
   * @api
   */


  Text.prototype.setPlacement = function (placement) {
    this.placement_ = placement;
  };
  /**
   * Set whether to rotate the text with the view.
   *
   * @param {boolean} rotateWithView Rotate with map.
   * @api
   */


  Text.prototype.setRotateWithView = function (rotateWithView) {
    this.rotateWithView_ = rotateWithView;
  };
  /**
   * Set the fill.
   *
   * @param {import("./Fill.js").default} fill Fill style.
   * @api
   */


  Text.prototype.setFill = function (fill) {
    this.fill_ = fill;
  };
  /**
   * Set the rotation.
   *
   * @param {number|undefined} rotation Rotation.
   * @api
   */


  Text.prototype.setRotation = function (rotation) {
    this.rotation_ = rotation;
  };
  /**
   * Set the scale.
   *
   * @param {number|import("../size.js").Size|undefined} scale Scale.
   * @api
   */


  Text.prototype.setScale = function (scale) {
    this.scale_ = scale;
    this.scaleArray_ = (0, _size.toSize)(scale !== undefined ? scale : 1);
  };
  /**
   * Set the stroke.
   *
   * @param {import("./Stroke.js").default} stroke Stroke style.
   * @api
   */


  Text.prototype.setStroke = function (stroke) {
    this.stroke_ = stroke;
  };
  /**
   * Set the text.
   *
   * @param {string|Array<string>|undefined} text Text.
   * @api
   */


  Text.prototype.setText = function (text) {
    this.text_ = text;
  };
  /**
   * Set the text alignment.
   *
   * @param {string|undefined} textAlign Text align.
   * @api
   */


  Text.prototype.setTextAlign = function (textAlign) {
    this.textAlign_ = textAlign;
  };
  /**
   * Set the text baseline.
   *
   * @param {string|undefined} textBaseline Text baseline.
   * @api
   */


  Text.prototype.setTextBaseline = function (textBaseline) {
    this.textBaseline_ = textBaseline;
  };
  /**
   * Set the background fill.
   *
   * @param {import("./Fill.js").default} fill Fill style.
   * @api
   */


  Text.prototype.setBackgroundFill = function (fill) {
    this.backgroundFill_ = fill;
  };
  /**
   * Set the background stroke.
   *
   * @param {import("./Stroke.js").default} stroke Stroke style.
   * @api
   */


  Text.prototype.setBackgroundStroke = function (stroke) {
    this.backgroundStroke_ = stroke;
  };
  /**
   * Set the padding (`[top, right, bottom, left]`).
   *
   * @param {Array<number>|null} padding Padding.
   * @api
   */


  Text.prototype.setPadding = function (padding) {
    this.padding_ = padding;
  };

  return Text;
}();

var _default = Text;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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